<template>
    <div class="article-blog-page" @scroll="scrolling()">
        <breadcrumbs
            :breadcrumbs="breadcrumbs"
            :scroll="scroll"
            @goBack="back"
        />
        <section class="article-blog__banner article-blog__banner--eclipse">
            <img
                class="article-blog__banner-img"
                src="@/assets/blog/openai-account-banned-for-policy-violations.jpg"
                alt="OpenAI account banned for policy violations you never committed"
                loading="lazy"
                modernize='sizes=["768w", "1200w", "1536w", "1920w", "2400w", "3840w", "original"] imageFormats=["webp", "original"]'
            />
            <h1 class="article-blog__banner-title">
                OpenAI Account Got Banned For Policy Violations You Never Committed
            </h1>
        </section>
        <div class="container article-blog section_holder">
            <div class="section_title">
                <SidebarBlog :tagFromPost="tags" @searchByTag="goToPath"/>
            </div>
            <div class="section_content">
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        The problem
                    </h2>
                    <p>
                        Picture this. You've got a chatbot or an AI tool running on OpenAI. Traffic is growing, the product makes money, everything is calm. Then an email lands: your organization is violating the Usage Policies. There's activity linked to harassment, threats, sexual abuse, sexual content, sometimes even child exploitation. The first email is easy to miss in a busy inbox. You might not get a second one. The account just goes dark.
                    </p>
                    <p>
                        And here's the trap. The first thing almost everyone does after that first email is rotate the keys. It's the obvious move, and it's exactly what OpenAI tells you to do. Usually the noise stops, so you close the ticket and move on. But rotating keys only fixes one cause: a leaked key. If the real source was a public endpoint someone is abusing, or a file-upload pipeline letting bad content through, the flags keep building quietly in the background. By the time the second signal lands you've long forgotten about it, and the second time there's usually no warning, just a dead account.
                    </p>
                    <p>
                        The worst part is that in most of these cases, the person who built the product didn't do anything wrong. At Wdevs we run into this often during audits, so let's walk through why it happens, what the market offers to prevent it, and how we close the gap for our own clients.
                    </p>
                    <p>
                        And here's the other hard part: once it happens, reaching a human at OpenAI is difficult. Most of what answers you is automated, and getting the decision actually re-reviewed by a person takes persistence.
                    </p>
                    <p>
                        If you're in the EU there's one extra lever, though a limited one. Under Article 22 of the GDPR you have the right not to be subject to a decision based solely on automated processing where it has legal or similarly significant effects, plus the right to ask for human review and contest it. Two caveats. It protects individuals, not companies, so if the account belongs to a legal entity the argument is weak. And even where it applies, getting OpenAI to act on it is slow. Treat it as a backstop, not a plan. The real answer is not to get deactivated in the first place.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        What's actually going on
                    </h2>
                    <p>
                        OpenAI, like every other LLM provider, runs automatic moderation on all traffic that goes through the API. By their own account they use an internal version of <b>/moderations</b> for this: prompts, completions, and uploads are constantly checked against the Usage Policies. Whole categories of content are off-limits: sexual content, child exploitation, violence and self-harm, scams, hate and harassment, spam.
                    </p>
                    <p>
                        Here's the part people miss. You're on the hook not just for your own requests, but for what every one of your end users does. The policy says it plainly: <em>"We require organizations to use OpenAI's services in line with our usage policies, including the use of our services by any of their end-users."</em>
                    </p>
                    <p>
                        After that it's probability. Nobody outside OpenAI knows exactly how their scoring works: which patterns get flagged, at what threshold, how violations stack up. But the mechanism is real. Somewhere in their system your account starts lighting up, and once the signal is strong enough you get a warning first, then a shutdown.
                    </p>
                    <p>
                        And that's the hole. If you offer public or free access to inference without hard filtering, anyone can deliberately spam your chat with garbage. Point a bot at your input box, feed it "kill yourself," banned content, sexualized material about minors, and your account collects flags. For an attacker it's a cheap way to knock out a competitor. Costs them nothing, and your working product disappears.
                    </p>
                    <p>
                        This isn't hypothetical. There's <a href="https://community.openai.com/t/need-help-facing-openai-usage-violation-due-to-users-abuse/1004947" target="_blank" rel="noopener">a telling case on the OpenAI Developer Community</a>: a developer built a free set of AI tools on GPT-4o mini and got an email about a violation tied specifically to exploitation, harm, or sexualization of children. He generated nothing of the sort and reasonably concluded that a competitor had abused his platform to trigger the ban.
                    </p>
                    <p>
                        One more thing worth saying: this doesn't only hit small hobby projects. We know of an e-commerce client on OpenAI Tier 5, paying several thousand dollars a month, whose account got suspended over suspected sexual content. There was nothing like that in his product. Budget and tier buy you no immunity here.
                    </p>
                    <p>
                        One category deserves a special mention: anything touching sexualization or exploitation of minors. Providers treat it as zero-tolerance, which in practice means it skips the usual warning-then-suspension ladder. A single strong signal can deactivate an account outright, with little or no appeal. That's also exactly why it's the category an attacker aims for when they want to take you down. It's the fastest path to a hard ban and the hardest one to argue your way back from.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Why "but I already have moderation" doesn't save you
                    </h2>
                    <p>
                        The usual reaction is "I plugged in the Moderation API, how could I possibly get banned?" There are a few subtle failure points, and real cases from the community show them well. A developer in <a href="https://community.openai.com/t/despite-double-moderation-my-openai-account-got-suspended-any-advice/1148772" target="_blank" rel="noopener">one community thread</a> did what looked like everything right: IP rate limiting, his own banned-word filter, and double moderation with OpenAI's Moderation API followed by Gemini's safety settings. The account still got shut down. The discussion surfaced the usual mistakes.
                    </p>
                    <ul>
                        <li><b>Input moderated, output ignored.</b> Plenty of people moderate the input but never the output. The danger isn't only in what the user typed. It's also in what the model generated. Filter the input alone and you leave half the attack surface wide open.</li>
                        <li><b>The endpoint isn't the platform's internal check.</b> The moderation endpoint isn't perfect and doesn't line up one-to-one with OpenAI's internal system. It chunks and processes text its own way. Trim or pad the input and you can get a noticeably different verdict, so something that clears your <b>/moderations</b> call isn't guaranteed to clear the platform's internal check. The reverse happens too: it can flag harmless words like "romantic" or "adult" out of context.</li>
                        <li><b>Open, free inference is the most dangerous form of service.</b> If any anonymous user can hammer your backend, you're expected to do everything and then some to moderate both input and output. One layer won't cut it.</li>
                    </ul>
                    <p>
                        This matters more than ever. The moment any URL goes public, bots start probing it for weaknesses within minutes, and an unprotected LLM proxy is an immediate target. It doesn't help that a lot of this glue code is now vibe-coded. AI-assisted code ships fast and often ships holes with it, and vibe-coding in particular is a bottomless source of vulnerabilities. That may change, but today it's hard to argue with.
                    </p>
                    <p>
                        There's a specific version of this that bites people. You moderate the main chat carefully, and you miss the side doors. Most LLM products have secondary endpoints: history or memory summarization, embeddings, a "regenerate" or "improve this" helper. All of those also send user text to the model. If any of them takes text straight from the request and forwards it unmoderated, an attacker can skip your chat entirely and hit that endpoint directly. That's your hidden proxy. The fix is to map every place where text reaches the model, not just the obvious one, and make sure each either moderates its own input or only ever receives text that was already moderated upstream.
                    </p>
                    <p>
                        The takeaway is simple. Moderation isn't a checkbox you tick and forget. It's an architectural layer you design on purpose and re-audit as new failure patterns show up.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        What the market offers
                    </h2>
                    <p>
                        Before I get to our approach, let's be fair about the tools already out there. There's no shortage of them, and each has its niche.
                    </p>
                    <ul>
                        <li><b>OpenAI Moderation API</b> — the free endpoint that classifies content into violation categories. OpenAI's own baseline advice is to run user input through it before the request reaches the LLM, and to check the model's responses too. To avoid a latency hit, teams often run the check asynchronously alongside the main call: if moderation trips, return a placeholder; if not, return the model's answer. It's the right place to start, but on its own it's not enough, as the cases above show.</li>
                        <li><b>OpenAI Guardrails</b> — a newer open framework from OpenAI for input and output "rails": checks for jailbreaks, prompt injection, off-limits topics. Handy as a preset rule set sitting on top of moderation.</li>
                        <li><b>NeMo Guardrails (NVIDIA)</b> — an open-source toolkit (Apache 2.0) with its own language, Colang, for defining policies. It intercepts and validates input and output at several stages of the pipeline, aims for low latency, and works across providers.</li>
                        <li><b>Llama Guard &amp; Prompt Guard (Meta)</b> — an open classifier model (1B and 8B versions) you can self-host to tag input and output, plus a small classifier aimed specifically at prompt injection and jailbreaks. Prompt Guard answers "is someone trying to break the system," Llama Guard answers "is this content off-limits." A good fit when self-hosting and data control matter to you.</li>
                        <li><b>Lakera Guard</b> — a commercial real-time "AI firewall." One API call screens input and output for prompt injection, jailbreaks, PII leaks, malicious links, and inappropriate content. People pick it when prompt injection is the main threat and low latency is a hard requirement.</li>
                        <li><b>Azure AI Content Safety / AWS Bedrock Guardrails</b> — Microsoft's and Amazon's cloud moderation, content filters and denied-topic rules you configure inside Azure AI Foundry or Bedrock. The obvious pick if your stack already lives in that cloud.</li>
                        <li><b>Guardrails AI</b> — a platform with a library of 60-plus ready-made validators you can chain into pipelines and attach to your output structure.</li>
                        <li><b>Framework-native moderation (e.g. ChatKit)</b> — genuinely blocks some requests natively before they reach the LLM. That's good, but it's one line of defense, and leaning on it alone is risky. Its settings need a periodic re-check against fresh cases too.</li>
                    </ul>
                    <p>
                        None of these tools solves the whole problem by itself. What works is defense in depth: several layers, each tuned to the specific product.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        How we handle it at Wdevs
                    </h2>
                    <p>
                        After a handful of clients came to us with the same problem, an account flagged for suspected policy violations covering harassment, threats, sexual abuse, and sexual content, we settled on an approach we put on top of the existing integration. Worth stressing: even when a client already uses something like ChatKit, where requests are moderated natively before hitting the LLM, we still push to spend a day re-checking everything against what we've learned from recent cases. A day of work beats losing a live account. Here's what we actually do.
                    </p>
                    <ul>
                        <li><b>A separate check layer in front of the LLM.</b> Any request that breaks any policy gets cut off before it reaches the model. Instead of a generation, it returns a controlled reject, something like "I can only help with questions about the company's products." That way banned content never reaches inference, and the account stops collecting flags.</li>
                        <li><b>We narrow the product down to its own domain.</b> A bot that answers strictly within its job (products, support, a specific subject area) gives far less room for abuse than an open mouth with access to free inference. Limiting the topic and the input length is both a safety move and a defense against prompt injection.</li>
                        <li><b>We moderate input and output.</b> We check not only what the user sent but what the model is about to reply. That closes the second half of the attack surface, the half people most often get burned on.</li>
                        <li><b>We fail closed.</b> If the moderation call itself fails, whether it's the network, the key, or a timeout, we block the request rather than let it through. When content safety is the priority, a broken detector should fail shut, not open.</li>
                        <li><b>We tag requests with a safety identifier.</b> Every call carries a <b>safety_identifier</b>, a stable, hashed ID for the end user. This is OpenAI's own recommendation: it lets the platform trace abuse to a specific user instead of nuking your whole account, and it lets you score users yourself and block the ones who keep abusing the service.</li>
                        <li><b>We build in resilience against a single provider's problems.</b> We're developing a setup that can switch to a different LLM when one provider has trouble. Providers don't share the same policies or the same automated thresholds, so if one issues a warning, you can shift legitimate traffic to another while you investigate and fix the root cause with the first. The service keeps running instead of going dark on the first strike. To be clear, this is continuity while you resolve the issue, not a way to keep routing the same unmoderated traffic somewhere else. If the content is genuinely abusive, you'll trip the next provider too.</li>
                        <li><b>We keep an audit trail of rejected requests.</b> Every blocked request is logged separately with its reason, meaning the moderation categories that tripped. If it ever comes to an appeal, that log is the evidence that we were filtering bad input all along, and it shows which user (by IP or identifier) sent it, rather than suggesting the whole integration is dirty.</li>
                        <li><b>We run an audit and red-teaming.</b> Before we call it done, we push the product through adversarial scenarios, trying to break it the way a bad-faith user or a competitor would, and we close whatever we find.</li>
                    </ul>
                    <p>
                        From there the work splits into two clear stages. First the fix itself: put in the check layer, patch input and output, re-verify the native moderation. Then, if needed, the buildout: user scoring, multi-provider fallback, ongoing monitoring.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        If it already happened
                    </h2>
                    <p>
                        If you're reading this after the email has already landed, a few things matter more than the rest.
                    </p>
                    <ul>
                        <li><b>Don't lose the first warning.</b> It's easy to miss, and it may be the only one you get. Read it, keep it, treat it as the clock starting.</li>
                        <li><b>Work out where the traffic came from.</b> Pull your own request logs and compare them against your OpenAI usage or a data export. If the platform shows more traffic than your app ever generated, your key is being used off-platform and rotating it is the right first move. If the volumes match, the problem is in what your own endpoint let through, and new keys won't fix it.</li>
                        <li><b>File the appeal, then don't stop there.</b> Templates and auto-responses are the norm, and the first reply is often a flat denial. What actually moves cases is a human seeing them. A clear, specific post on the OpenAI Developer Community has pulled more than one account back from a decision that looked final.</li>
                        <li><b>Bring evidence.</b> If you kept a log of rejected requests with reasons, and tagged calls with a per-user <b>safety_identifier</b>, you can show two things at once: that you were filtering bad input, and that any abuse traces to specific users rather than your whole integration. That's a far stronger position than "we didn't do anything wrong."</li>
                    </ul>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        What to do right now
                    </h2>
                    <p>
                        If you've got an LLM integration in production, here's the minimum checklist. Make sure moderation sits on both the input and the output, not just one of them. Close off anonymous or free access to inference, or at least clamp it down hard with rate limits, auth, and an input-length cap. Add a <b>safety_identifier</b> to every call so one user's abuse can't sink the whole account. Don't ignore the first warning from your provider; it's easy to lose, and there may not be a second one. And re-audit your moderation settings every so often against new cases, rather than treating it as a one-time "set it and forget it."
                    </p>
                    <p>
                        If you can, put a bot check (Cloudflare Turnstile) or a WAF in front, or require login. A public endpoint with no gate is the single biggest risk factor. And while you're in there, close the ordinary web holes on that same endpoint: path traversal, SSRF, and leaking raw model output or stack traces in error responses.
                    </p>
                    <p>
                        An account ban caused by someone else's malicious behavior is a real and expensive risk, and it hits small products and big paying clients alike. <a href="/ai-solutions/#openai-account-ban-protection">The good news is it's manageable</a>. Layered moderation and sane architecture take most of the threat off the table.
                    </p>
                    <p>
                        If you'd like us to look at your integration and put a protective layer in place, <a href="/contacts">get in touch</a>. A day of audit is usually a lot cheaper than the downtime from a dead account.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">
                        Sources
                    </h2>
                    <ul>
                        <li><a href="https://openai.com/policies/usage-policies/" target="_blank" rel="noopener">Usage policies — OpenAI</a></li>
                        <li><a href="https://help.openai.com/en/articles/10562188-why-was-my-openai-account-deactivated" target="_blank" rel="noopener">Why Was My OpenAI Account Deactivated? — OpenAI Help Center</a></li>
                        <li><a href="https://help.openai.com/en/articles/8940831-how-we-identify-problematic-content-on-our-services-for-individuals" target="_blank" rel="noopener">How we identify problematic content on our services — OpenAI Help Center</a></li>
                        <li><a href="https://community.openai.com/t/need-help-facing-openai-usage-violation-due-to-users-abuse/1004947" target="_blank" rel="noopener">Need Help: Facing OpenAI Usage Violation Due to user's Abuse — OpenAI Developer Community</a></li>
                        <li><a href="https://community.openai.com/t/despite-double-moderation-my-openai-account-got-suspended-any-advice/1148772" target="_blank" rel="noopener">Despite Double Moderation, My OpenAI Account Got Suspended — OpenAI Developer Community</a></li>
                        <li><a href="https://developers.openai.com/cookbook/examples/how_to_use_moderation" target="_blank" rel="noopener">How to use the moderation API — OpenAI Cookbook</a></li>
                        <li><a href="https://developers.openai.com/api/docs/guides/safety-best-practices" target="_blank" rel="noopener">Safety best practices — OpenAI API</a></li>
                        <li><a href="https://developers.openai.com/cookbook/examples/how_to_use_guardrails" target="_blank" rel="noopener">How to implement LLM guardrails — OpenAI Cookbook</a></li>
                        <li><a href="https://guardrails.openai.com/" target="_blank" rel="noopener">OpenAI Guardrails</a></li>
                        <li><a href="https://github.com/NVIDIA/NeMo-Guardrails" target="_blank" rel="noopener">NeMo Guardrails — NVIDIA</a></li>
                        <li><a href="https://generalanalysis.com/guides/best-ai-guardrails" target="_blank" rel="noopener">Best AI Guardrails in 2026 — General Analysis</a></li>
                    </ul>
                </div>
                <div>
                    <div class="footer-blog-item">
                        <div
                            class="btn-read-more primary contacts-button small"
                            id="show-modal"
                            @click="this.$router.push('/blog')"
                        >
                            <span class="button-content">
                                <span class="icon">
                                    <img
                                    v-svg-inline
                                    :data-src="require('@/assets/icons/rarr-icon.svg')"
                                    alt="" />
                                </span>
                                Read more our stories
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SidebarBlog from "./Sidebar.vue";
import Breadcrumbs from "/src/components/Breadcrumbs";
import {mapState} from "vuex";

export default {
    name: "BlogOpenAiAccountBan",
    data() {
        return {
            backHistory: history.state.back,
            tags:['AI Agents','LLM','OpenAI','Cyber Security','Production','Business Automation'],
            scroll: false,
            position: null,
            breadcrumbs: [
                {
                    link: '/',
                    name: 'Home',
                },
                {
                    name: 'Blog',
                    link: '/blog',
                },
                {
                    name: 'OpenAI account banned for policy violations',
                },
            ],
        };
    },
    components: {
        SidebarBlog,
        Breadcrumbs,
    },
    computed:{
        ...mapState(['blog'])
    },
    methods: {
        goToPath(tag){
            let post = this.blog.find(item => item.tags.includes(tag))
            this.$router.push(post.img_src)
        },
        back(){
            if(!document.referrer || this.backHistory !== '/blog'){
                Object.defineProperty(document, "referrer", {
                    get: function () {
                        return `${window.location.origin}/blog`;
                    },
                });
                this.$router.push('/blog')
                return
            } else{
                this.$router.go(-1)
            }
        },
        scrolling() {
            const scroll = document.documentElement.scrollTop;
            if (scroll > 50) {
                this.scroll = true;
            } else {
                this.scroll = false;
            }
        },
    },
    created() {
        window.history.scrollRestoration = "auto";
        window.addEventListener("scroll", this.scrolling);
    },
    beforeUnmount() {
        window.removeEventListener("scroll", this.scrolling);
    },
}
</script>

<style lang="scss">
@import "../../scss/_variables.scss";
@import "include-media/dist/_include-media.scss";
.article-blog-header {
    .article-blog-review__title {
        padding: 120px 0;
        text-align: end;
        text-transform: uppercase;
        color: $black;
        font-size: 32px;
        line-height: 111%;
        margin: 0;
        @include media(">=tablet") {
            font-size: 40px;
            max-width: 100%;
        }
        @include media(">=tablet-xl") {
            font-size: 56px;
        }
        @include media(">=widescreen") {
            font-size: 64px;
            max-width: 100%;
        }
    }
}

.footer-blog-item {
    display: block;
    padding-top: 20px;
    @include media(">=tablet") {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .article-blog-tags {
        margin-top: 40px;
        color: $red;
        font-weight: 700;
        font-size: 16px;
        line-height: 28px;
        @include media(">=tablet") {
            margin-top: 0;
            text-align: center;
        }
        span {
            margin-right: 20px;
            @include media(">=widescreen") {
                margin-right: 40px;
            }
        }
    }
}

.article-blog-page {
    a {
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }
    .link{
        color:$red;
        cursor:pointer;
        &:hover{
            text-decoration: underline;
        }
    }
    .btn-read-more {
        .button-content {
            border-radius: 26px;
            padding: 2px 12px 3px;
            background-color: $red;
            color: $white;
            font-weight: 700;
            font-size: 16px;
            line-height: 28px;
            cursor: pointer;
            width: max-content;
            background-image: linear-gradient($darkRed, $darkRed);
            background-repeat: no-repeat;
            transition: background-size 0s, color 0s;
            background-size: 0 100%;
            @include media(">=tablet-xl") {
                padding: 6px 24px 8px;
            }
            &:hover {
                background-size: 100% 100%;
                color: $white;
            }
        }
        .icon {
            padding-top: 6px;
            margin-right: 15px;
            transform: rotateY(180deg);
        }
        a {
            color: $white;
            text-decoration: none;
            &:hover {
                text-decoration: underline;
            }
        }
    }

    .go-back-to-read {
        padding: 16px 0;
        border-bottom-left-radius: 26px 26px;
        border-bottom-right-radius: 26px 26px;
        position: fixed;
        background: $white;
        z-index: 50;
        width: 100vw;
        transition: all 0.3s;
        @include media(">=tablet-xl") {
            padding: 16px 0;
        }
        &.scrolling {
            padding: 5px 0;
            border-bottom: 1px solid $red;
            @include media(">=tablet-xl") {
                padding: 5px 0;
            }
        }
    }
}
.article-blog {
    &.section_holder {
        display: flex;
        flex-direction: column-reverse;
        @include media(">=tablet-xl") {
            flex-direction: row;
        }
    }
    .section_title {
        margin-right: 0;
        width: 100%;
        @include media(">=tablet-xl") {
            margin-right: 43px;
            width: 35%;
        }
    }
    .section_content {
        margin-bottom: 60px;
        display: block;
        margin-left: 0;
        width: 100%;
        a {
            color: $red;
        }
        &__item {
            margin-bottom: 40px;
            @include media(">=tablet") {
                margin-bottom: 60px;
            }
            @include media(">=desktop") {
                margin-bottom: 120px;
            }
            &-title {
                &-icon {
                    flex-shrink: 0;
                    width: 400px;
                    height: auto;
                    margin-right: 15px;
                    @media all and (max-width: 480px) {
                        width: 100%;
                        margin-right: 0;
                    }
                    svg {
                        max-width: 100%;
                        width: 100%;
                        max-height: 100%;
                        height: 100%;
                    }
                }
                &--icon {
                    display: flex;
                    align-items: flex-end;
                    @media all and (max-width: 767px) {
                        flex-wrap: wrap;
                    }
                    @media (min-width: 992px) and (max-width: 1199px) {
                        flex-wrap: wrap;
                    }
                }
            }
            &-slider {
                position: relative;
            }
            &-link {
                display: inline-block;
                &:hover {
                    text-decoration: none;
                }
            }
        }
        @include media(">=tablet-xl") {
            margin-left: 37px;
            width: 65%;
            margin-bottom: 0;
        }
        &__paragraph {
            display: flex;
            margin-bottom: 45px;
            &-icon {
                flex-shrink: 0;
                display: inline-block;
                width: 65px;
                height: 65px;
                margin-right: 10px;
                vertical-align: middle;
                @media all and (min-width: 768px) {
                    width: 100px;
                    height: 100px;
                    margin-right: 15px;
                }
                svg {
                    max-width: 100%;
                    width: 100%;
                    max-height: 100%;
                    height: 100%;
                }
            }
        }
        &__list {
            &--two-columns {
                columns: 2;
                -webkit-columns: 2;
                -moz-columns: 2;
            }
            &--two-columns-tablet-desktop {
                @media all and (min-width: 768px) {
                    columns: 2;
                    -webkit-columns: 2;
                    -moz-columns: 2;
                }
            }
        }
    }
}

.article-blog__banner {
    position: relative;
    height: 300px;
    margin-bottom: 60px;
    @include media(">=tablet") {
        height: 500px;
    }
    @include media(">=desktop") {
        height: 600px;
        margin-bottom: 120px;
    }
    &--eclipse {
        z-index: 1;
        &:before {
            content: "";
            position: absolute;
            z-index: 2;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background-color: rgba(0,0,0,0.3);
        }
        .article-blog__banner-title {
            z-index: 3;
        }
    }
    &-img,
    picture {
        display: block;
        height: 100%;
        img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }
}
.article-blog__banner-title {
    text-transform: uppercase;
    color: $white;
    font-size: 32px;
    line-height: 111%;
    position: absolute;
    bottom: 20px;
    left: 16px;
    max-width: calc(100% - 32px);
    margin: 0;
    @include media(">=tablet") {
        bottom: 40px;
        font-size: 40px;
        left: 40px;
        max-width: calc(100% - 80px);
    }
    @include media(">=tablet-xl") {
        bottom: 80px;
        left: 40px;
        font-size: 56px;
        max-width: calc(100% - 80px);
    }
    @include media(">=widescreen") {
        bottom: 80px;
        left: 80px;
        font-size: 64px;
        max-width: 1150px;
    }
}
.article-blog__img,
picture {
    margin: 0 0 40px;
    display: block;
}
.article-blog__customer {
    em {
        display: block;
        margin-top: 32px;
    }
    .button {
        margin-top: 40px;
    }
}
</style>
